import { Link } from "react-router-dom";
import { Server, Code2, ArrowUpRight } from "lucide-react";
import Hero from "@/components/site/Hero";
import Ticker from "@/components/site/Ticker";
import Testimonials from "@/components/site/Testimonials";
import { IMAGES, Reveal } from "@/components/site/anim";

const CARDS = [
  {
    icon: Server,
    tag: "Service 01",
    title: "Managed IT Services",
    desc: "Proactive monitoring, security and support for your entire stack — the IT department you always wished you had.",
    img: IMAGES.network,
  },
  {
    icon: Code2,
    tag: "Service 02",
    title: "Custom Software",
    desc: "Web platforms, internal tools and integrations engineered around your workflow. Design, build, ship, iterate.",
    img: IMAGES.code,
  },
];

export default function Home() {
  return (
    <>
      <Hero />
      <Ticker />

      {/* What we do teaser */}
      <section id="what" data-testid="home-what" className="bg-base py-24 md:py-32 px-6 md:px-10">
        <div className="mx-auto max-w-[1600px]">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
            <div>
              <Reveal as="p" className="font-mono text-xs uppercase tracking-[0.25em] text-accent mb-5">
                What we do
              </Reveal>
              <Reveal delay={0.1} as="h2" className="font-heading font-light uppercase tracking-tight text-4xl md:text-5xl lg:text-6xl leading-[0.95]">
                Two disciplines.<br />
                <span className="text-[#71717A]">One relentless standard.</span>
              </Reveal>
            </div>
            <Reveal delay={0.2}>
              <Link
                data-testid="home-view-services"
                to="/services"
                className="group inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.15em] text-[#A1A1AA] hover:text-white transition-colors duration-300"
              >
                All services
                <ArrowUpRight className="h-4 w-4 group-hover:rotate-45 transition-transform duration-300" />
              </Link>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {CARDS.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.1}>
                <Link
                  data-testid={`home-service-card-${i}`}
                  to="/services"
                  className="group relative block overflow-hidden rounded-2xl border border-white/10 bg-surface min-h-[420px] p-8 md:p-10 flex flex-col justify-end hover:border-white/25 transition-colors duration-500"
                >
                  <img src={c.img} alt={c.title} className="absolute inset-0 h-full w-full object-cover opacity-30 group-hover:opacity-45 group-hover:scale-105 transition-all duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-[#0A0A0B]/60 to-transparent" />
                  <div className="relative">
                    <c.icon className="h-9 w-9 text-accent mb-6" />
                    <div className="font-mono text-xs tracking-[0.2em] text-[#71717A] mb-3">{c.tag}</div>
                    <h3 className="font-heading text-3xl md:text-4xl font-medium tracking-tight mb-4">{c.title}</h3>
                    <p className="text-[#A1A1AA] max-w-md">{c.desc}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Testimonials />

      {/* CTA band */}
      <section data-testid="home-cta" className="bg-[#0A0A0B] border-t border-white/10 py-24 md:py-32 px-6 md:px-10">
        <div className="mx-auto max-w-[1600px] flex flex-col lg:flex-row lg:items-center lg:justify-between gap-10">
          <Reveal as="h2" className="font-heading font-black uppercase tracking-tighter leading-[0.9] text-5xl md:text-7xl lg:text-8xl text-glow">
            Ready when<br />you are<span className="text-accent">.</span>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              data-testid="home-cta-button"
              to="/contact"
              className="group inline-flex items-center gap-3 rounded-full bg-accent text-black px-8 py-5 font-mono text-xs uppercase tracking-[0.15em] hover:bg-white transition-colors duration-300"
            >
              Start a project
              <ArrowUpRight className="h-4 w-4 group-hover:rotate-45 transition-transform duration-300" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
